import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { BaseApiService } from '../../shared/base-service/base-api.service';
import { WipTransactionFee } from '../../shared/models/fee/wip-transaction-fee.model';
import { DriverMVRTransaction } from '../models/driver-mvr-transaction.model';
import { DriverTransactionReviewQueueWorkItem } from '../models/driver-transaction-review-queue-work-item.model';
import { StandAloneCommercialSkillTransaction } from '../models/stand-alone/stand-alone-commercial-skill-transaction.model';
import { StandAloneKnowledgeTransaction } from '../models/stand-alone/stand-alone-knowledge-transaction.model';
import { StandAloneMotorcycleSkillTransaction } from '../models/stand-alone/stand-alone-motorcycle-skill-transaction.model';
import { StandAloneNonCommercialSkillTransaction } from '../models/stand-alone/stand-alone-non-commercial-skill-transaction.model';
import { StandAloneVisionScreeningTransaction } from '../models/stand-alone/stand-alone-vision-screening-transaction.model';
import { StandAloneVoterRegistrationTransaction } from '../models/stand-alone/stand-alone-voter-registration-transaction.model';

@Injectable()
export class DriverTransactionService extends BaseApiService {
    /**
     * Calculates fees for the current transaction.
     */
    public calculateFees = (businessTransactionId: number): Observable<{ result: WipTransactionFee[] } | null> => {
        if (businessTransactionId) {
            return this.get(`Driver/BusinessTransaction/${businessTransactionId}/CalculateFees`);
        }

        return of(null);
    };

    public calculateFeesWithLogging = (businessTransactionId: number): Observable<{ result: WipTransactionFee[] } | null> => {
        if (businessTransactionId) {
            return this.get(`Driver/BusinessTransaction/${businessTransactionId}/CalculateFeesWithLogging`);
        }

        return of(null);
    };

    /**
     * Updates fees for the current transaction.
     */
    public updateFees = (businessTransactionId: number, fees) => this.post(`Driver/BusinessTransaction/fee/${businessTransactionId}`, fees);

    /**
     * Calculates fees for the current transaction.
     */
    public getFeeLookups = () => this.get('Driver/BusinessTransaction/fee/lookup');

    public getProductLookups = () => this.get('Foundation/BusinessTransactionProduct/Products');

    public getProductFeeLookups = () => this.get('Foundation/BusinessTransactionProduct/ProductFees');

    /**
     * Calculates fees for the current transaction.
     */
    public getFees = (businessTransactionId: number) => this.get(`Driver/BusinessTransaction/fee/${businessTransactionId}`);

    public getAdjustmentReasonTypesForTransaction = (businessTransactionNumberId: number): Observable<{ result: any }> => this.get(`Driver/BusinessTransaction/AdjustmentReasonTypes/${businessTransactionNumberId}`);

    public getKnowledgeTransaction = (businessTransactionId: number): Observable<StandAloneKnowledgeTransaction> => this.get(`Driver/BusinessTransaction/knowledge/${businessTransactionId}`);

    public saveKnowledgeTransaction = (businessTransactionId: number, transaction: StandAloneKnowledgeTransaction): Observable<boolean> => this.post(`Driver/BusinessTransaction/knowledge/${businessTransactionId}`, transaction);

    public getMotorcycleSkillTransaction = (businessTransactionId: number): Observable<StandAloneMotorcycleSkillTransaction> => this.get(`Driver/BusinessTransaction/MotorcycleSkill/${businessTransactionId}`);

    public saveMotorcycleSkillTransaction = (businessTransactionId: number, transaction: StandAloneMotorcycleSkillTransaction): Observable<boolean> => this.post(`Driver/BusinessTransaction/MotorcycleSkill/${businessTransactionId}`, transaction);

    /**
     * Gets the commercial skill transaction by business transaction id.
     */
    public getCommercialSkillTransaction = (businessTransactionId: number): Observable<StandAloneCommercialSkillTransaction> => this.get(`Driver/BusinessTransaction/CommercialSkill/${businessTransactionId}`);

    /**
     * Saves the standalone commercial skill transaction.
     */
    public saveCommercialSkillTransaction = (businessTransactionId: number, transaction: StandAloneCommercialSkillTransaction): Observable<boolean> => this.post(`Driver/BusinessTransaction/CommercialSkill/${businessTransactionId}`, transaction);

    public getNonCommercialSkillTransaction = (businessTransactionId: number): Observable<StandAloneNonCommercialSkillTransaction> => this.get(`Driver/BusinessTransaction/NonCommercialSkill/${businessTransactionId}`);

    public saveNonCommercialSkillTransaction = (businessTransactionId: number, transaction: StandAloneNonCommercialSkillTransaction): Observable<boolean> => this.post(`Driver/BusinessTransaction/NonCommercialSkill/${businessTransactionId}`, transaction);

    /**
     * Gets the vision screening transaction by business transaction id.
     */
    public getVisionScreeningTransaction = (businessTransactionId: number): Observable<StandAloneVisionScreeningTransaction> => this.get(`Driver/BusinessTransaction/VisionScreening/${businessTransactionId}`);

    /**
     * Saves the standalone vision screening transaction.
     */
    public saveVisionScreeningTransaction = (businessTransactionId: number, transaction: StandAloneVisionScreeningTransaction): Observable<boolean> => this.post(`Driver/BusinessTransaction/VisionScreening/${businessTransactionId}`, transaction);

    public getVoterRegistrationTransaction = (businessTransactionId: number): Observable<StandAloneVoterRegistrationTransaction> => this.get(`Driver/BusinessTransaction/VoterRegistration/${businessTransactionId}`);

    public validateTransaction = (businesstransactionId: number): Observable<any> => this.get(`Driver/BusinessTransaction/DriverValidation/${businesstransactionId}`);

    public getDriverMVRTransaction = (businessTransactionId: number): Observable<DriverMVRTransaction> => this.get(`Driver/BusinessTransaction/DriverMVR/${businessTransactionId}`);

    public saveDriverMVRTransaction = (businessTransactionId: number, transaction: DriverMVRTransaction): Observable<DriverMVRTransaction> => this.post(`Driver/BusinessTransaction/DriverMVR/${businessTransactionId}`, transaction);

    public getDriverTransactionReviewQueue = (startIndex: number, numberOfRowsToRetrieve: number, driverTransactionReviewStatusTypeIds: number[], customerNumberId?: number): Observable<any> => {

        let requestUri = `Driver/CredentialIssuanceApplication/DriverTransactionReviewWorkItemQueue/${startIndex}/${numberOfRowsToRetrieve}`;
        if (!!customerNumberId) {
            requestUri += `/${customerNumberId}`;
        }

        let queryParams = [];

        if (!!driverTransactionReviewStatusTypeIds && driverTransactionReviewStatusTypeIds.length > 0) {
            driverTransactionReviewStatusTypeIds.forEach((id) => {
                queryParams.push(`driverTransactionReviewStatusTypeIds=${id}`);
            });
        }

        if (0 != queryParams.length) {
            requestUri = `${requestUri}?${queryParams.join('&')}`;
        }

        return this.get(requestUri);
    };

    public getDriverTransactionReviewStatusTypes = (): Observable<any> => this.get('Driver/CredentialIssuanceApplication/DriverTransactionReviewStatusTypes');

    public updateDriverTransactionReviewWorkItem = (workItem: DriverTransactionReviewQueueWorkItem): Observable<any> => this.post('Driver/CredentialIssuanceApplication/DriverTransactionReviewWorkItem/', workItem);

    /**
     * Gets the status of the transaction for finalization
     * @param businessTransactionNumberId: Business transaction id to get the status for.
     * @return List of errors for finalization
     */
    public getTransactionFinalizationStatus = (businessTransactionNumberId: number) => this.get('Driver/BusinessTransaction/' + businessTransactionNumberId + '/Finalize/Status');

    public getADCReviewQueue = (adcReviewWorkItemStatusTypeIds: number[]): Observable<{ result: any }> => {
        let requestUri = 'Driver/Credential/ADC/Review/Queue';
        let queryParams = [];

        if (!!adcReviewWorkItemStatusTypeIds && adcReviewWorkItemStatusTypeIds.length > 0) {
            adcReviewWorkItemStatusTypeIds.forEach((id) => {
                queryParams.push(`adcReviewWorkItemStatusTypeIds=${id}`);
            });
        }

        if (0 != queryParams.length) {
            requestUri = `${requestUri}?${queryParams.join('&')}`;
        }

        return this.get(requestUri);
    };

    public getADCWorkItemsForCustomer = (customerNumberId: number): Observable<{ result: any }> => this.get(`Driver/Credential/ADC/Review/Queue/${customerNumberId}`);

    public getADCReviewWorkItemStatusTypes = (): Observable<any> => this.get('Driver/Credential/ADC/Review/StatusTypes');

    public updateADCReviewWorkItem = (model: any): Observable<any> => this.post('Driver/Credential/ADC/Review/Update', model);

    public getQueueHistory = (businessTransactionNumberId: number): Observable<any> => this.get(`Driver/CredentialIssuanceApplication/QueueHistory/${businessTransactionNumberId}`);

    public getCustomerTransactionDetail(customerNumberId: number): Observable<any> {
        return this.get(`Driver/external/AAMVA/${customerNumberId}/transaction-detail`);
    }

    public getTransactionDetail(customerNumberId: number, transactionTypeId: number, processTypeId: number) {
        return this.get(`Driver/external/AAMVA/${customerNumberId}/${transactionTypeId}/${processTypeId}/transaction-detail`);
    }
}
